import type { EventRsvpQuestion } from '@/models/event';

import { apiClient } from './client';
import { patchEventDetailRsvpQuestions } from './events';
import { queryClient } from './queryClient';
import { QuestionType } from './questionTypes';
import type { components } from './types.gen';

export type RsvpQuestionType = components['schemas']['EventRsvpQuestionIn']['field_type'];
export const DEFAULT_RSVP_QUESTION_TYPE: RsvpQuestionType = QuestionType.Textarea;

/** Client-only draft ids; sent as null on sync so the server assigns UUIDs. */
export const DRAFT_RSVP_QUESTION_ID_PREFIX = 'q-' as const;

export function isDraftRsvpQuestionId(id: string): boolean {
  return id.startsWith(DRAFT_RSVP_QUESTION_ID_PREFIX);
}

export function newRsvpQuestionId(): string {
  return `${DRAFT_RSVP_QUESTION_ID_PREFIX}${crypto.randomUUID()}`;
}

interface WireQuestion {
  id: string;
  label: string;
  field_type: RsvpQuestionType;
  options?: string[];
  required: boolean;
  display_order?: number;
}

export function mapRsvpQuestion(w: WireQuestion): EventRsvpQuestion {
  return {
    id: w.id,
    label: w.label,
    fieldType: w.field_type,
    options: w.options ?? [],
    required: w.required,
  };
}

function questionPayload(question: EventRsvpQuestion, newIdsAsNull: boolean) {
  return {
    id: newIdsAsNull && isDraftRsvpQuestionId(question.id) ? null : question.id,
    label: question.label,
    field_type: question.fieldType,
    options: question.options,
    required: question.required,
  };
}

/** Replace questions atomically and return canonical server ids/order. */
export async function syncEventRsvpQuestions(
  eventId: string,
  next: readonly EventRsvpQuestion[],
  previous: readonly EventRsvpQuestion[],
): Promise<EventRsvpQuestion[]> {
  const { data } = await apiClient.put<WireQuestion[]>(
    `/api/community/events/${eventId}/rsvp-questions/`,
    {
      expected: previous.map((question) => questionPayload(question, false)),
      questions: next.map((question) => questionPayload(question, true)),
    },
  );
  const questions = data.map(mapRsvpQuestion);
  // UpdateEvent caches EventOut before this sync runs; without this patch the
  // RSVP dialog keeps stale/empty questions while the API still requires answers.
  patchEventDetailRsvpQuestions(queryClient, eventId, questions);
  return questions;
}
